// Параметры канала WhatsApp и готовые тексты сообщений.
// Числа взяты из коммерческого предложения (WhatsApp Cloud API, август 2026).
// Контакты и списки рассылки — в constants/whatsapp-contacts.js.

// Лимит текста шаблона WhatsApp вместе с подставленными значениями.
export const BODY_MAX_LENGTH = 1024;

// Деньги: цену сообщения и расход считает сервер по данным Meta (GET /api/whatsapp/pricing),
// здесь только подписи.
const CURRENCY_SIGN = { EUR: '€', USD: '$', GBP: '£', UAH: 'грн' };
export const currencySign = (code) => CURRENCY_SIGN[code] || code || '';

// Как называется категория в расходах Meta.
export const PRICING_CATEGORY_LABEL = {
  MARKETING: 'Маркетинг',
  UTILITY: 'Служебные',
  AUTHENTICATION: 'Аутентификация',
  AUTHENTICATION_INTERNATIONAL: 'Аутентификация (международная)',
  SERVICE: 'Ответы клиентам в окне 24 часа',
  MARKETING_LITE: 'Маркетинг Lite',
};

// «3,58» из числа, с копейками только если они есть.
export const moneyLabel = (value, digits = 2) =>
  Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: digits });

// Правильная форма слова «контакт»: 1 контакт, 2 контакта, 5 контактов.
export const contactsLabel = (count) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) {
    return `${count} контактов`;
  }
  if (last === 1) {
    return `${count} контакт`;
  }
  if (last >= 2 && last <= 4) {
    return `${count} контакта`;
  }
  return `${count} контактов`;
};

// Статусы шаблона в Meta. kind задаёт цвет бейджа: ok / warn / error.
export const TEMPLATE_STATUS = {
  approved: { label: 'Одобрен Meta', kind: 'ok' },
  pending: { label: 'На проверке Meta', kind: 'warn' },
  in_appeal: { label: 'Апелляция', kind: 'warn' },
  paused: { label: 'Приостановлен', kind: 'warn' },
  rejected: { label: 'Отклонён Meta', kind: 'error' },
  disabled: { label: 'Отключён', kind: 'error' },
};

export const statusInfo = (status) =>
  TEMPLATE_STATUS[status] || { label: status || 'Неизвестно', kind: 'warn' };

export const TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Маркетинг', hint: 'Цены, акции, новости, предложения' },
  { value: 'UTILITY', label: 'Служебные', hint: 'Заказы, оплата, статус сделки' },
];

// Кнопки приветственного шаблона-запроса согласия. Те же тексты, что на сервере
// (helpers/consent.js): по ним Meta создаёт кнопки, а предпросмотр их показывает.
export const CONSENT_BUTTON_TEXTS = {
  uk: ['Так, надсилайте', 'Ні, не надсилайте'],
  ru: ['Да, присылайте', 'Нет, не присылайте'],
  en: ['Yes, send me updates', 'No, do not send'],
  en_US: ['Yes, send me updates', 'No, do not send'],
};

export const TEMPLATE_LANGUAGES = [
  { value: 'uk', label: 'Украинский' },
  { value: 'ru', label: 'Русский' },
  { value: 'en_US', label: 'Английский (США)' },
  { value: 'en', label: 'Английский' },
];

export const FOOTER_MAX_LENGTH = 60;
export const TEMPLATE_NAME_PATTERN = /^[a-z0-9_]{1,512}$/;

export const placeholderNumbers = (text) => {
  const found = [];
  text.replace(/\{\{(\d+)\}\}/g, (match, n) => {
    found.push(Number(n));
    return match;
  });
  return found;
};

// Правило Meta: слов + переменных не меньше 3 * переменных + 1.
export const ratioInfo = (body) => {
  const vars = placeholderNumbers(body).length;
  const words = body
    .replace(/\{\{\d+\}\}/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return { words, vars, total: words + vars, required: 3 * vars + 1 };
};

// Meta не принимает в значении переменной переносы строк, табы и больше
// четырёх пробелов подряд.
export const sanitizeParam = (value) =>
  String(value == null ? '' : value)
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();

export const fallbackName = 'шановний клієнте';

// Значения переменных для одного получателя в порядке {{1}}, {{2}}, ...
export const paramsFor = (template, values, contact) =>
  template.params.map((param) =>
    sanitizeParam(
      param.auto === 'contactName'
        ? (contact && contact.name) || fallbackName
        : values[param.key]
    )
  );

// Длина текста шаблона без значения переменной с номером index (с нуля):
// сколько места остаётся под это поле в лимите BODY_MAX_LENGTH.
export const bodyLengthWithout = (template, params, index) =>
  template.body.replace(/\{\{(\d+)\}\}/g, (match, n) =>
    Number(n) - 1 === index ? '' : params[Number(n) - 1] || match
  ).length;

// Текст сообщения, каким его увидит клиент.
export const renderTemplate = (template, params) =>
  template.body.replace(/\{\{(\d+)\}\}/g, (match, index) => {
    const value = params[Number(index) - 1];
    return value ? value : match;
  });

// Почему Meta не доставила сообщение, по-русски. Ключ — код ошибки Meta; для
// записей, сохранённых до появления кода, ищем по фрагменту её английского текста.
const FAILURE_REASONS = {
  131049: {
    match: 'ecosystem engagement',
    title: 'Meta не доставила маркетинговое сообщение этому получателю',
    hint:
      'Так Meta ограничивает рекламные сообщения тем, кто мало реагирует на ' +
      'сообщения компаний. Это решение Meta, а не ошибка рассылки. Клиенты, ' +
      'которые вам отвечали, получают такие сообщения без проблем.',
  },
  131026: {
    match: 'undeliverable',
    title: 'Сообщение не может быть доставлено',
    hint:
      'Номера нет в WhatsApp, получатель не принял условия WhatsApp или у ' +
      'него слишком старая версия приложения.',
  },
  131047: {
    match: 're-engagement',
    title: 'Прошло больше 24 часов с последнего сообщения клиента',
    hint: 'Писать свободным текстом уже нельзя, можно отправить только шаблон.',
  },
  131042: {
    match: 'payment',
    title: 'Проблема с оплатой аккаунта WhatsApp Business',
    hint: 'Проверьте способ оплаты в Meta: «Счета и оплата».',
  },
  131048: {
    match: 'spam rate limit',
    title: 'Meta ограничила отправку из-за жалоб на спам',
    hint: 'Слишком много жалоб или блокировок от получателей. Снизьте частоту рассылок.',
  },
  131056: {
    match: 'pair rate limit',
    title: 'Слишком много сообщений этому получателю за короткое время',
    hint: 'Повторите отправку позже.',
  },
  130429: {
    match: 'rate limit hit',
    title: 'Превышен лимит запросов к WhatsApp',
    hint: 'Повторите отправку через несколько минут.',
  },
  132001: {
    match: 'template does not exist',
    title: 'Шаблон не найден или не одобрен',
    hint: 'Проверьте на странице «Шаблоны», что шаблон одобрен и язык указан верно.',
  },
  132000: {
    match: 'number of parameters',
    title: 'Число значений не совпало с числом переменных шаблона',
    hint: 'Заполните все поля шаблона.',
  },
  132012: {
    match: 'parameter format',
    title: 'Значение переменной в неверном формате',
    hint: 'В значении нельзя переносы строк, табы и больше четырёх пробелов подряд.',
  },
  132015: {
    match: 'paused',
    title: 'Шаблон приостановлен Meta из-за низкого качества',
    hint: 'Дождитесь снятия паузы или создайте новый шаблон.',
  },
  132016: {
    match: 'disabled',
    title: 'Шаблон отключён Meta',
    hint: 'Создайте новый шаблон.',
  },
  131031: {
    match: 'account has been locked',
    title: 'Аккаунт WhatsApp Business заблокирован',
    hint: 'Проверьте уведомления в Meta Business.',
  },
};

// error — строка вида «[131049] текст Meta» или просто текст. Всегда возвращает
// объект: объяснение по-русски (если код известен) и оригинал для поддержки.
export const explainFailure = (error) => {
  const text = String(error || '').trim();
  const code = (text.match(/^\[(\d+)\]/) || [])[1];
  const original = text.replace(/^\[\d+\]\s*/, '');
  const lower = original.toLowerCase();

  const byCode = code && FAILURE_REASONS[code];
  const found =
    byCode ||
    Object.values(FAILURE_REASONS).find((item) => lower.includes(item.match));

  return {
    title: found ? found.title : 'Сообщение не доставлено',
    hint: found ? found.hint : '',
    original: original ? (code ? `${code}: ${original}` : original) : '',
  };
};
